// public/v3/src/lib/activity-log.js — read-side helpers for patient activity
// logs. The monthly push flow appends a synthetic consolidated entry tagged
// { isConsolidatedNote: true } (see lib/push-month.js); display, totals, and
// AI-suggest scans must ignore those — otherwise pushed months show double
// the count + double the minutes, and the synthetic's notes can falsely
// "satisfy" content-based detections.
//
// Module format: CommonJS (module.exports). Jest reads this file directly
// (no babel-jest preset-env installed), and esbuild interops CommonJS
// modules cleanly when consumed via `import { x } from './activity-log'`
// from JSX pages — same pattern as lib/workflow.js and lib/panel-review.js.

const { entryProgramFamily } = require('./bhi-billing');

function isContributorEntry(entry) {
  return !!entry && !entry.isConsolidatedNote;
}

// Returns the contributor entries (non-consolidated) for the given month.
// If monthKey is falsy, returns all contributor entries.
function entriesForMonth(activityLog, monthKey) {
  if (!Array.isArray(activityLog)) return [];
  return activityLog.filter((e) => {
    if (!isContributorEntry(e)) return false;
    if (!monthKey) return true;
    return typeof e.date === 'string' && e.date.slice(0, 7) === monthKey;
  });
}

// Sums minutes across the given entries. Tolerates either `mins` (prototype
// shape) or `duration` (v1 backend shape) — and double-checks the consolidated
// filter so callers that pass an unfiltered array still get the right number.
function sumMinutes(entries) {
  if (!Array.isArray(entries)) return 0;
  return entries
    .filter(isContributorEntry)
    .reduce((s, e) => s + (Number(e.mins) || Number(e.duration) || 0), 0);
}

// Filters activity-log entries to the given month AND program family.
// Family is derived from the CPT code in `entry.program` via
// `entryProgramFamily` (single source of truth — see bhi-billing.js).
// When program === 'ccm', legacy untagged entries (no `program` field)
// also match — preserves backward compatibility with logs written before
// the program-tag rollout. When program === 'bhi', the entry's CPT code
// must be in BHI_PROGRAM_CODES (currently '99484' or 'G0570'). Skips
// consolidated synthetic entries.
function entriesForMonthByProgram(activityLog, monthKey, program) {
  if (!Array.isArray(activityLog)) return [];
  const prefix = monthKey || '';
  const target = program || 'ccm';
  return activityLog.filter((e) => {
    if (!e || typeof e !== 'object') return false;
    if (!(e.date || '').startsWith(prefix)) return false;
    if (e.isConsolidatedNote) return false;
    return entryProgramFamily(e) === target;
  });
}

module.exports = {
  isContributorEntry,
  entriesForMonth,
  sumMinutes,
  entriesForMonthByProgram,
};
